import { Button, Card, Flex } from '@aws-amplify/ui-react';
import type { Edulead } from 'edulead';
import { useCallback } from 'react';
import { TbArrowRight, TbInfoCircle } from 'react-icons/tb';
import Select from 'react-select';
import { getEnumFieldInfo } from '../utils/enum-field-values';
import General from '../utils/GeneralUtils';
import { CrmMappableField } from '../utils/hubspot-field-config';
// TODO: Re-enable when ready
// import { ConfirmCreateFieldModal, CreateFieldDetails } from './ConfirmCreateFieldModal';
import { CreateFieldDetails } from './ConfirmCreateFieldModal';
import './CrmFieldMappings.scss';
import { EnumValuesSlideout, useEnumValuesSlideout } from './EnumValuesSlideout';
import LoadingBars from './LoadingBars';
import UniversalTag from './UniversalTag';
import { WarningBadge } from './WarningBadge';

/**
 * CRM field types that each Edulead field type may be mapped onto.
 *
 * Keyed by our own `CrmFieldType`. The values are raw type names as reported by
 * the CRM's describe API, and the two CRMs use different vocabularies -
 * Salesforce reports 'picklist' / 'double' / 'boolean', HubSpot reports
 * 'enumeration' / 'number' / 'bool' - so both appear in the same list.
 *
 * Every entry begins with the Edulead type's own name, so any mapping that was
 * valid under the previous exact-match rule remains valid.
 */
const CRM_TYPE_COMPATIBILITY: Record<string, string[]> = {
  // Picklists accept a plain string on write, so a text source maps onto them safely.
  string: ['string', 'picklist', 'multipicklist', 'combobox', 'textarea', 'enumeration'],
  number: ['number', 'double', 'int', 'currency', 'percent'],
  // A boolean source maps onto a Salesforce picklist, where the export writes Yes/No.
  // HubSpot's 'enumeration' is absent: its company export omits nulls rather than writing
  // the negative, so the value set would be incomplete there.
  bool: ['bool', 'boolean', 'booleancheckbox', 'picklist', 'combobox'],
  datetime: ['datetime', 'date'],
  email: ['email', 'string'],
  phone: ['phone', 'phone_number', 'string'],
  url: ['url', 'string', 'textarea']
};

/**
 * Whether an Edulead field type may be mapped onto a given CRM field type.
 * Unknown types are permitted rather than blocked, matching the previous behaviour.
 */
function isCrmTypeCompatible(eduleadType: string | undefined, crmType: string | undefined): boolean {
  if (!eduleadType || !crmType) return true;
  const compatible = CRM_TYPE_COMPATIBILITY[eduleadType];
  if (!compatible) return true;
  return compatible.includes(crmType);
}

export interface CrmProperty {
  name: string;
  label: string;
  type: string;
  fieldType?: string;
  description?: string;
  groupName?: string;
  readOnlyValue?: boolean;
}

/**
 * A CRM field from the Go API (`ListIntegrationProperties`) as the editor shows it. Only HubSpot's
 * read-only flag reaches `readOnlyValue`: the Salesforce editor never marked a field read-only.
 */
export function crmPropertyOf(p: Edulead.EduleadV4IntegrationProperty, integration: 'hubspot' | 'salesforce'): CrmProperty {
  return {
    name: p.id ?? '',
    label: p.label ?? '',
    type: p.type ?? '',
    description: p.description || undefined,
    groupName: p.group_name || undefined,
    readOnlyValue: integration === 'hubspot' ? (p.read_only ?? false) : undefined,
  };
}

/** One object type's mappings from the Go API as the editor holds them: Edulead field → CRM field. */
export function mappingRecord(mappings: Edulead.EduleadV4IntegrationFieldMapping[] | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  for (const m of mappings ?? []) {
    if (m.edulead_field) out[m.edulead_field] = m.crm_field ?? '';
  }
  return out;
}

/** One object type's mappings as the editor shows them now, the whole list UpdateIntegration replaces. */
export function mappingList(record: Record<string, string>): Edulead.EduleadV4IntegrationFieldMapping[] {
  return Object.entries(record)
    .filter(([, crmField]) => crmField)
    .map(([edulead_field, crm_field]) => ({ edulead_field, crm_field }));
}

export type MappingOperation = 'create' | 'update' | 'delete';

export interface MappingChange {
  eduleadField: string;
  crmField: string;
  objectType: 'CONTACT' | 'INSTITUTION';
  operation: MappingOperation;
}

export interface CrmFieldMappingsProps {
  /** Title for the field mappings section */
  title: string;
  /** Description text shown below the title */
  description?: string;
  /** Whether the component is in loading state */
  isLoading: boolean;
  /** Whether the component is in saving state */
  isSaving: boolean;
  /** Whether the component is read-only */
  readOnly?: boolean;
  /** Error message for properties loading */
  propertiesLoadError?: string | null;
  /** Company/Account fields configuration */
  companyFields: CrmMappableField[];
  /** Contact fields configuration */
  contactFields: CrmMappableField[];
  /** Available CRM properties for company/account object */
  availableCompanyProperties: CrmProperty[];
  /** Available CRM properties for contact object */
  availableContactProperties: CrmProperty[];
  /** Field mappings for company/account fields (eduleadField -> crmField) */
  companyFieldMappings: Record<string, string>;
  /** Field mappings for contact fields (eduleadField -> crmField) */
  contactFieldMappings: Record<string, string>;
  /** Original field mappings for company/account fields (for computing diff) */
  originalCompanyFieldMappings: Record<string, string>;
  /** Original field mappings for contact fields (for computing diff) */
  originalContactFieldMappings: Record<string, string>;
  /** Callback when a field mapping changes */
  onFieldMappingChange: (eduleadField: string, crmField: string, isCompanyField: boolean) => void;
  /** Callback when save is clicked, receives only the changed mappings with operations */
  onSave: (changes: MappingChange[]) => void;
  /** Callback when refresh is clicked */
  onRefresh?: () => void;
  /** Label for company fields section (e.g., "Company Fields" or "Account Fields") */
  companyFieldsLabel?: string;
  /** Label for contact fields section */
  contactFieldsLabel?: string;
  /** Placeholder text for dropdowns */
  placeholder?: string;
  /** Custom validation function to check if a CRM field is valid */
  validateCrmField?: (crmField: string, eduleadField: string, isCompanyField: boolean) => { valid: boolean; error?: string };
  /** Callback to create a field in the CRM. If provided, shows "Create field" for creatable unmapped fields */
  onCreateField?: (field: CrmMappableField, isCompanyField: boolean) => Promise<{ fieldApiName: string }>;
  /** Details to show in the create field confirmation modal */
  getCreateFieldDetails?: (field: CrmMappableField, isCompanyField: boolean) => CreateFieldDetails;
  /** CRM name for modal title (e.g., "Salesforce", "HubSpot") */
  crmName?: string;
}

export function CrmFieldMappings({
  title,
  description,
  isLoading,
  isSaving,
  readOnly = false,
  propertiesLoadError,
  companyFields,
  contactFields,
  availableCompanyProperties,
  availableContactProperties,
  companyFieldMappings,
  contactFieldMappings,
  originalCompanyFieldMappings,
  originalContactFieldMappings,
  onFieldMappingChange,
  onSave,
  onRefresh,
  companyFieldsLabel = 'Company Fields',
  contactFieldsLabel = 'Contact Fields',
  placeholder = 'Select CRM field...',
  validateCrmField,
  crmName = 'your CRM',
  // TODO: Re-enable when ready
  // onCreateField,
  // getCreateFieldDetails,
}: CrmFieldMappingsProps) {
  // Drawer listing the values an enum field exports, for building a matching picklist
  const enumSlideout = useEnumValuesSlideout();

  // State for field creation
  // TODO: Re-enable when ready
  // const [creatingFieldFor, setCreatingFieldFor] = useState<string | null>(null);
  // const [confirmModalField, setConfirmModalField] = useState<CrmMappableField | null>(null);
  // const [confirmModalIsCompanyField, setConfirmModalIsCompanyField] = useState(false);

  // Compute diff between original and current mappings, returning actual change objects
  function computeMappingChanges(
    current: Record<string, string>,
    original: Record<string, string>,
    objectType: 'CONTACT' | 'INSTITUTION'
  ): MappingChange[] {
    const changes: MappingChange[] = [];

    // Check current mappings for creates and updates
    for (const [field, crmField] of Object.entries(current)) {
      if (!crmField) continue; // Skip empty mappings
      const originalCrmField = original[field];
      if (!originalCrmField) {
        changes.push({ eduleadField: field, crmField, objectType, operation: 'create' });
      } else if (originalCrmField !== crmField) {
        changes.push({ eduleadField: field, crmField, objectType, operation: 'update' });
      }
    }

    // Check for removals (was mapped, now empty or missing)
    for (const [field, originalCrmField] of Object.entries(original)) {
      if (!originalCrmField) continue; // Skip already empty
      const currentCrmField = current[field];
      if (!currentCrmField) {
        changes.push({ eduleadField: field, crmField: originalCrmField, objectType, operation: 'delete' });
      }
    }

    return changes;
  }

  const companyChanges = computeMappingChanges(companyFieldMappings, originalCompanyFieldMappings, 'INSTITUTION');
  const contactChanges = computeMappingChanges(contactFieldMappings, originalContactFieldMappings, 'CONTACT');
  const allChanges = [...companyChanges, ...contactChanges];

  const totalDiff = {
    created: allChanges.filter(c => c.operation === 'create').length,
    updated: allChanges.filter(c => c.operation === 'update').length,
    removed: allChanges.filter(c => c.operation === 'delete').length,
  };
  const hasChanges = allChanges.length > 0;

  function getSaveButtonLabel(): string {
    if (isSaving) return 'Saving...';
    if (!hasChanges) return 'Save Field Mappings';

    const parts: string[] = [];
    if (totalDiff.created > 0) parts.push(`${totalDiff.created} new`);
    if (totalDiff.updated > 0) parts.push(`${totalDiff.updated} updated`);
    if (totalDiff.removed > 0) parts.push(`${totalDiff.removed} removed`);
    return `Save (${parts.join(', ')})`;
  }

  // Format option label with styled datatype
  const formatOptionLabel = useCallback((option: { value: string; label: string; type?: string; isReadOnly?: boolean }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span>
        {option.label}{option.isReadOnly && ' (Read-only)'}
      </span>
      {option.type && <span className="desc-12" style={{ marginLeft: 8 }}>{option.type}</span>}
    </div>
  ), []);

  function isCrmFieldValid(crmField: string, eduleadField: string, isCompanyField: boolean): boolean {
    if (validateCrmField) {
      return validateCrmField(crmField, eduleadField, isCompanyField).valid;
    }

    const properties = isCompanyField ? availableCompanyProperties : availableContactProperties;

    // If properties couldn't be loaded, assume field is valid
    if (properties.length === 0 && propertiesLoadError) {
      return true;
    }

    return properties.some(prop => prop.name === crmField);
  }

  function getCrmFieldValidationError(crmField: string, eduleadField: string, isCompanyField: boolean): string | null {
    if (validateCrmField) {
      const result = validateCrmField(crmField, eduleadField, isCompanyField);
      return result.error || null;
    }

    const properties = isCompanyField ? availableCompanyProperties : availableContactProperties;

    if (properties.length === 0 && propertiesLoadError) {
      return null;
    }

    const allFields = [...companyFields, ...contactFields];
    const requiredType = allFields.find(f => f.source === eduleadField)?.crmType;

    const field = properties.find(prop => prop.name === crmField);
    if (!field) {
      return 'This field does not exist in your CRM account';
    }

    if (!isCrmTypeCompatible(requiredType, field.type)) {
      return `Field type mismatch: expected ${requiredType}, but this field is ${field.type}`;
    }

    return null;
  }

  function isCrmFieldUsedElsewhere(crmField: string, eduleadField: string, isCompanyField: boolean): boolean {
    // Check for duplicates within the same object type
    const mappings = isCompanyField ? companyFieldMappings : contactFieldMappings;
    return Object.entries(mappings).some(([key, value]) =>
      key !== eduleadField && value === crmField
    );
  }

  function isCrmFieldReadOnly(crmField: string, isCompanyField: boolean): boolean {
    const properties = isCompanyField ? availableCompanyProperties : availableContactProperties;
    const prop = properties.find(p => p.name === crmField);
    return prop?.readOnlyValue === true;
  }

  function getCrmFieldOptions(eduleadField: string, isCompanyField: boolean) {
    const properties = isCompanyField ? availableCompanyProperties : availableContactProperties;
    const mappings = isCompanyField ? companyFieldMappings : contactFieldMappings;

    // Get the required CRM datatype for the selected Edulead field
    const fields = isCompanyField ? companyFields : contactFields;
    const requiredType = fields.find(f => f.source === eduleadField)?.crmType;


    // Get already used CRM fields within the same object type (excluding the current field)
    const usedCrmFields = new Set(
      Object.entries(mappings)
        .filter(([key]) => key !== eduleadField)
        .map(([, value]) => value)
        .filter(Boolean)
    );

    // Filter properties by required type and convert to react-select format
    const options = properties
      .filter(prop => isCrmTypeCompatible(requiredType, prop.type))
      .map(prop => ({
        value: prop.name,
        label: prop.name,
        type: prop.type,
        isDisabled: usedCrmFields.has(prop.name) || prop.readOnlyValue === true,
        isReadOnly: prop.readOnlyValue === true,
        description: prop.description,
        groupName: prop.groupName
      }));

    return options;
  }

  function getValidationErrors(): string[] {
    const errors: string[] = [];

    // Check for required company field mappings
    const requiredCompanyFields = companyFields.filter(f => f.required);
    for (const field of requiredCompanyFields) {
      if (!companyFieldMappings[field.source]) {
        errors.push(`${field.label} is required.`);
      }
    }

    // Check for required contact field mappings
    const requiredContactFields = contactFields.filter(f => f.required);
    for (const field of requiredContactFields) {
      if (!contactFieldMappings[field.source]) {
        errors.push(`${field.label} is required.`);
      }
    }

    // Check for duplicate CRM fields within each object type
    const usedCompanyFields = Object.values(companyFieldMappings).filter(f => f);
    const hasCompanyDuplicates = usedCompanyFields.some((field, index) =>
      usedCompanyFields.indexOf(field) !== index
    );
    if (hasCompanyDuplicates) {
      errors.push('Each company CRM field can only be mapped once.');
    }

    const usedContactFields = Object.values(contactFieldMappings).filter(f => f);
    const hasContactDuplicates = usedContactFields.some((field, index) =>
      usedContactFields.indexOf(field) !== index
    );
    if (hasContactDuplicates) {
      errors.push('Each contact CRM field can only be mapped once.');
    }

    // Check that all company CRM fields exist in available properties
    for (const [eduleadField, crmField] of Object.entries(companyFieldMappings)) {
      if (!crmField) continue;
      if (availableCompanyProperties.length > 0 && !availableCompanyProperties.some(prop => prop.name === crmField)) {
        const fieldDef = companyFields.find(f => f.source === eduleadField);
        errors.push(`${fieldDef?.label || eduleadField}: CRM field "${crmField}" does not exist.`);
      }
    }

    // Check that all contact CRM fields exist in available properties
    for (const [eduleadField, crmField] of Object.entries(contactFieldMappings)) {
      if (!crmField) continue;
      if (availableContactProperties.length > 0 && !availableContactProperties.some(prop => prop.name === crmField)) {
        const fieldDef = contactFields.find(f => f.source === eduleadField);
        errors.push(`${fieldDef?.label || eduleadField}: CRM field "${crmField}" does not exist.`);
      }
    }

    // Check for read-only company fields
    for (const [eduleadField, crmField] of Object.entries(companyFieldMappings)) {
      if (!crmField) continue;
      if (isCrmFieldReadOnly(crmField, true)) {
        const fieldDef = companyFields.find(f => f.source === eduleadField);
        errors.push(`${fieldDef?.label || eduleadField}: "${crmField}" is a read-only field and cannot be updated.`);
      }
    }

    // Check for read-only contact fields
    for (const [eduleadField, crmField] of Object.entries(contactFieldMappings)) {
      if (!crmField) continue;
      if (isCrmFieldReadOnly(crmField, false)) {
        const fieldDef = contactFields.find(f => f.source === eduleadField);
        errors.push(`${fieldDef?.label || eduleadField}: "${crmField}" is a read-only field and cannot be updated.`);
      }
    }

    return errors;
  }

  function isValidForSave(): boolean {
    return getValidationErrors().length === 0;
  }

  function renderFieldRow(field: CrmMappableField, isCompanyField: boolean) {
    const mappings = isCompanyField ? companyFieldMappings : contactFieldMappings;
    const crmField = mappings[field.source] || '';
    const isFieldValid = !crmField || isCrmFieldValid(crmField, field.source, isCompanyField);
    const isDuplicate = crmField && isCrmFieldUsedElsewhere(crmField, field.source, isCompanyField);
    const isReadOnly = crmField && isCrmFieldReadOnly(crmField, isCompanyField);
    const properties = isCompanyField ? availableCompanyProperties : availableContactProperties;

    const selectedOption = crmField ? {
      value: crmField,
      label: (() => {
        const prop = properties.find(p => p.name === crmField);
        return prop ? prop.name : crmField;
      })(),
      type: (() => {
        const prop = properties.find(p => p.name === crmField);
        return prop?.type;
      })()
    } : null;

    const isMissingRequired = field.required && !crmField;
    const hasWarning = isMissingRequired || (crmField && (!isFieldValid || isDuplicate || isReadOnly));
    const warningMessage = hasWarning
      ? (isMissingRequired
        ? 'Required field must be mapped'
        : isReadOnly
          ? 'This is a read-only field and cannot be updated via API'
          : isDuplicate
            ? 'This field is already mapped to another Edulead field'
            : getCrmFieldValidationError(crmField, field.source, isCompanyField))
      : null;

    return (
      <div key={field.source} className="crm-field-mappings__field-row">
        <div className="crm-field-mappings__field-label">
          <span className="crm-field-mappings__field-name">
            {field.label}
            {getEnumFieldInfo(field.source) && (
              <button
                type="button"
                className="crm-field-mappings__enum-values-button"
                title={`Show the ${field.label} values to configure as a picklist`}
                onClick={() => enumSlideout.open(field.source)}
              >
                <TbInfoCircle size={15} />
              </button>
            )}
          </span>
          <span className="desc-12">{field.crmType}</span>
        </div>
        <div className="crm-field-mappings__arrow"><TbArrowRight /></div>
        <div className="crm-field-mappings__field-input">
          <div className="crm-field-mappings__select-wrapper">
            <Select
              value={selectedOption}
              onChange={(newValue) => {
                if (!readOnly) onFieldMappingChange(field.source, newValue?.value || '', isCompanyField);
              }}
              options={getCrmFieldOptions(field.source, isCompanyField)}
              formatOptionLabel={formatOptionLabel}
              isClearable={!readOnly}
              isSearchable={!readOnly}
              isDisabled={readOnly}
              placeholder={placeholder}
              menuPlacement="auto"
              styles={General.getCreatableStylesCompact()}
              components={readOnly ? { DropdownIndicator: () => null, IndicatorSeparator: () => null } : undefined}
            />
          </div>
          <div className="crm-field-mappings__field-actions">
            {field.required && <UniversalTag category="REQUIRED" />}
            {hasWarning && (
              <WarningBadge
                tooltip={warningMessage || ''}
                text={isReadOnly ? 'Read-only field' : undefined}
                size={18}
              />
            )}
            {/* TODO: Re-enable when ready
            {field.creatable && !crmField && onCreateField && getCreateFieldDetails && (
              <Link
                style={{ fontSize: 12, whiteSpace: 'nowrap' }}
                onClick={() => openCreateFieldModal(field, isCompanyField)}
              >
                {creatingFieldFor === field.source ? 'Creating...' : 'Create field'}
              </Link>
            )}
            */}
          </div>
        </div>
      </div>
    );
  }

  // TODO: Re-enable when ready
  // function openCreateFieldModal(field: CrmMappableField, isCompanyField: boolean) {
  //   setConfirmModalField(field);
  //   setConfirmModalIsCompanyField(isCompanyField);
  // }

  // TODO: Re-enable when ready
  // function closeCreateFieldModal() {
  //   setConfirmModalField(null);
  // }

  // async function handleCreateFieldConfirm() {
  //   if (!confirmModalField || !onCreateField) return;

  //   setCreatingFieldFor(confirmModalField.source);
  //   try {
  //     const result = await onCreateField(confirmModalField, confirmModalIsCompanyField);
  //     onFieldMappingChange(confirmModalField.source, result.fieldApiName);
  //     closeCreateFieldModal();
  //   } finally {
  //     setCreatingFieldFor(null);
  //   }
  // }

  // const modalFieldDetails = confirmModalField && getCreateFieldDetails
  //   ? getCreateFieldDetails(confirmModalField, confirmModalIsCompanyField)
  //   : null;

  return (
    <div className="crm-field-mappings">
      <Flex justifyContent="space-between" alignItems="center" style={{ marginBottom: 10 }}>
        <h3 style={{ margin: 0 }}>{title}</h3>
        {!readOnly && onRefresh && (
          <Button
            size="small"
            className="tiny"
            onClick={onRefresh}
            disabled={isLoading}
          >
            Refresh
          </Button>
        )}
      </Flex>

      {isLoading ? (
        <LoadingBars numberOfRows={10} />
      ) : (
        <>
          {description && (
            <div className="desc-10" style={{ marginBottom: 20 }}>
              {description}
            </div>
          )}
          <Card variation="outlined" style={{ padding: 20 }}>
            {/* Company/Account Fields Section */}
            {companyFields.length > 0 && (
              <div className="crm-field-mappings__section">
                <h4 className="crm-field-mappings__section-title">{companyFieldsLabel}</h4>
                <Flex direction="column" gap="12px">
                  {companyFields.map(field => renderFieldRow(field, true))}
                </Flex>
              </div>
            )}

            {/* Contact Fields Section */}
            {contactFields.length > 0 && (
              <div className="crm-field-mappings__section crm-field-mappings__section--contacts">
                <h4 className="crm-field-mappings__section-title">{contactFieldsLabel}</h4>
                <Flex direction="column" gap="12px">
                  {contactFields.map(field => renderFieldRow(field, false))}
                </Flex>
              </div>
            )}

            {!readOnly && (
              <Flex justifyContent="flex-end" className="crm-field-mappings__actions">
                <Button
                  size="small"
                  variation="primary"
                  onClick={() => onSave(allChanges)}
                  disabled={isSaving || !isValidForSave() || !hasChanges}
                >
                  {getSaveButtonLabel()}
                </Button>
              </Flex>
            )}
          </Card>
        </>
      )}

      {/* TODO: Re-enable when ready
      <ConfirmCreateFieldModal
        isOpen={!!confirmModalField}
        onClose={closeCreateFieldModal}
        onConfirm={handleCreateFieldConfirm}
        fieldDetails={modalFieldDetails}
        isLoading={!!creatingFieldFor}
        crmName={crmName}
      />
      */}

      <EnumValuesSlideout
        isOpen={enumSlideout.isOpen}
        onClose={enumSlideout.close}
        fieldKey={enumSlideout.fieldKey}
        crmName={crmName}
      />
    </div>
  );
}
